/**
 * Delar upp fritext från fastighetssystemen i text och länkar, så att länkar
 * kan renderas klickbara utan att någon HTML från källan någonsin tolkas.
 *
 * DH Marknad skriver länkar som `[[ https://… ]]` — så kom hyresvärdens
 * uthyrningspolicy fram, och visades ordagrant med hakparenteser. Formatet är
 * inte dokumenterat, så även `[[url|text]]` och `[[text|url]]` förstås. Vanliga
 * webbadresser i löptext görs också klickbara.
 *
 * Bara http(s) blir länkar. Allt annat — `javascript:`, `data:` och liknande —
 * lämnas som den text det var.
 */

export type TextSegment =
  | { kind: "text"; text: string }
  | { kind: "link"; href: string; label: string };

const DH_LINK = /\[\[([^\]]*?)\]\]/g;
const BARE_URL = /https?:\/\/[^\s<>"'\]]+/g;
/** Skiljetecken som avslutar en mening snarare än hör till adressen. */
const SENTENCE_PUNCTUATION = ".,;:!?";

/**
 * "Läs mer på https://exempel.se/policy." ska länka utan punkten. En
 * avslutande parentes hör till adressen bara om den också öppnas där
 * (Wikipedia-adresser), annars till texten runt om.
 */
function trimTrailingPunctuation(url: string): string {
  let end = url.length;
  while (end > 0) {
    const last = url[end - 1];
    if (SENTENCE_PUNCTUATION.includes(last)) {
      end -= 1;
      continue;
    }
    if (last === ")") {
      const head = url.slice(0, end);
      const opens = head.split("(").length - 1;
      const closes = head.split(")").length - 1;
      if (closes > opens) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return url.slice(0, end);
}

/** Adressen om den är en säker http(s)-länk, annars null. */
export function safeHref(candidate: string): string | null {
  const value = candidate.trim();
  if (!/^https?:\/\//i.test(value)) {
    return null;
  }
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? value : null;
  } catch {
    return null;
  }
}

function pushText(segments: TextSegment[], text: string) {
  if (!text) {
    return;
  }
  const last = segments[segments.length - 1];
  if (last?.kind === "text") {
    last.text += text;
  } else {
    segments.push({ kind: "text", text });
  }
}

function linkifyBareUrls(text: string, segments: TextSegment[]) {
  let cursor = 0;
  for (const match of text.matchAll(BARE_URL)) {
    const start = match.index ?? 0;
    const url = trimTrailingPunctuation(match[0]);
    const href = safeHref(url);
    pushText(segments, text.slice(cursor, start));
    if (href) {
      segments.push({ kind: "link", href, label: url });
    } else {
      pushText(segments, url);
    }
    cursor = start + url.length;
  }
  pushText(segments, text.slice(cursor));
}

/** `[[url]]`, `[[url|text]]` eller `[[text|url]]` som länk, annars null. */
function parseDhLink(inner: string): TextSegment | null {
  const parts = inner
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0 || parts.length > 2) {
    return null;
  }
  const urlIndex = parts.findIndex((part) => safeHref(part) !== null);
  if (urlIndex < 0) {
    return null;
  }
  const href = safeHref(parts[urlIndex])!;
  const label = parts.length === 2 ? parts[1 - urlIndex] : href;
  return { kind: "link", href, label };
}

export function parseLinks(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(DH_LINK)) {
    const start = match.index ?? 0;
    const link = parseDhLink(match[1]);
    if (!link) {
      // Inte en länk vi förstår: lämnas orörd hellre än att gissas.
      continue;
    }
    linkifyBareUrls(text.slice(cursor, start), segments);
    segments.push(link);
    cursor = start + match[0].length;
  }
  linkifyBareUrls(text.slice(cursor), segments);
  return segments;
}

/** Den enda länken om texten inte innehåller något annat än den, annars null. */
export function soleLink(
  segments: TextSegment[]
): Extract<TextSegment, { kind: "link" }> | null {
  const meaningful = segments.filter(
    (segment) => segment.kind === "link" || segment.text.trim().length > 0
  );
  return meaningful.length === 1 && meaningful[0].kind === "link"
    ? meaningful[0]
    : null;
}
